export type PageLinkType = '<<' | '<' | '...' | 'siblingPage' | 'currentPage' | '>' | '>>';

export interface PaginationLink {
  type: PageLinkType;
  page: number;
}
export const paginationLink = (type: PageLinkType, page: number): PaginationLink => ({ type, page });

function generateSiblingPageLinks(page: number, totalPages: number, nbSiblings: number = 2): PaginationLink[] {
  const links: PaginationLink[] = [];

  const maxNextSiblings = Math.min(totalPages - page, nbSiblings);
  const rightOffset = nbSiblings - maxNextSiblings;

  const firstGroupPage: number = Math.max(1, page - nbSiblings - rightOffset);
  const lastGroupPage: number = Math.min(totalPages, firstGroupPage + 2 * nbSiblings);

  if (firstGroupPage > 1) {
    links.push(paginationLink('...', firstGroupPage - 1));
  }

  for (let i = firstGroupPage; i < page; i++) {
    links.push(paginationLink('siblingPage', i));
  }

  links.push(paginationLink('currentPage', page));

  for (let i = page + 1; i <= lastGroupPage; i++) {
    links.push(paginationLink('siblingPage', i));
  }

  if (lastGroupPage < totalPages) {
    links.push(paginationLink('...', lastGroupPage + 1));
  }

  return links;
}

export function generatePaginationLinks(page: number, totalPages?: number, nbSiblings: number = 2) {
  if (!totalPages || totalPages <= 1) {
    return [];
  }

  const links: PaginationLink[] = [];

  if (page > 1) {
    links.push(paginationLink('<<', 1));
    links.push(paginationLink('<', page - 1));
  }

  links.push(...generateSiblingPageLinks(page, totalPages, nbSiblings));

  if (page < totalPages) {
    links.push(paginationLink('>', page + 1));
    links.push(paginationLink('>>', totalPages));
  }

  return links;
}

export function isFirstPageLink(link: PaginationLink) {
  return link.type === '<<';
}
export function isPreviousPageLink(link: PaginationLink) {
  return link.type === '<';
}
export function isNextPageLink(link: PaginationLink) {
  return link.type === '>';
}
export function isLastPageLink(link: PaginationLink) {
  return link.type === '>>';
}
export function isSeparatorLink(link: PaginationLink) {
  return link.type === '...';
}
export function isSiblingPageLink(link: PaginationLink) {
  return link.type === 'siblingPage';
}
export function isCurrentPageLink(link: PaginationLink) {
  return link.type === 'currentPage';
}

export function generatePaginationLinkDataE2E(link: PaginationLink): string {
  switch (link.type) {
    case '<<':
      return 'pagination-first';
    case '<':
      return 'pagination-previous';
    case '>':
      return 'pagination-next';
    case '>>':
      return 'pagination-last';
    default:
      return 'pagination-page';
  }
}
